import {
  KnowledgeBaseAdherenceCorrectness,
  type GradingKnowledgeBaseAdherence,
  type GradingKnowledgeBaseAdherenceStatement,
  type KnowledgeBaseAdherenceQuote,
} from "@repo/app-types/entities/knowledgeBaseAdherence";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import { Tab } from "@repo/ui/Link/routerLink";
import { DateTime, Duration } from "luxon";
import type { FC } from "react";
import { useMemo } from "react";
import {
  Collection,
  GridList,
  GridListHeader,
  GridListItem,
  GridListSection,
  TabList,
  TabPanel,
  Tabs,
  type Key,
} from "react-aria-components";
import { KnowledgeBaseAdherenceCorrectnessIcon } from "./knowledgeBaseAdherenceCorrectnessIcon.js";

export interface KnowledgeBaseAdherenceStatementsCardProps {
  recordings?: readonly AccountGradingRecording[];
  knowledgeBaseAdherence: GradingKnowledgeBaseAdherence | null | undefined;
  onStatementClick?: (statementIndex: number) => void;
}

interface GradingKnowledgeBaseAdherenceStatementSection {
  readonly id: Key;
  readonly recordedAt?: DateTime<true>;
  readonly items: readonly GradingKnowledgeBaseAdherenceStatementItem[];
}

interface MutableGradingKnowledgeBaseAdherenceStatementSection extends GradingKnowledgeBaseAdherenceStatementSection {
  id: Key;
  recordedAt?: DateTime<true>;
  items: GradingKnowledgeBaseAdherenceStatementItem[];
}

interface GradingKnowledgeBaseAdherenceStatementItem extends GradingKnowledgeBaseAdherenceStatement {
  readonly id: string;
  /** The index of this statement within the statements array. */
  readonly statementIndex: number;
  /** The quote used for grouping and sorting. */
  readonly primaryQuote: KnowledgeBaseAdherenceQuote | undefined;
}

const tabs = [
  {
    id: "incorrect",
    label: "Inaccuracies",
    correctness: KnowledgeBaseAdherenceCorrectness.Incorrect,
  },
  {
    id: "correct",
    label: "Correct",
    correctness: KnowledgeBaseAdherenceCorrectness.Correct,
  },
  {
    id: "unknown",
    label: "Unverifiable",
    correctness: KnowledgeBaseAdherenceCorrectness.Unknown,
  },
  {
    id: "all",
    label: "All",
    correctness: undefined,
  },
];

export const KnowledgeBaseAdherenceStatementsCard: FC<
  KnowledgeBaseAdherenceStatementsCardProps
> = (props) => {
  const { recordings, knowledgeBaseAdherence, onStatementClick } = props;

  const allItems: readonly GradingKnowledgeBaseAdherenceStatementItem[] =
    useMemo(() => {
      const reverseChronologicalRecordings = recordings?.toReversed();

      return (knowledgeBaseAdherence?.statements ?? []).map(
        (statement, index) => {
          // By default, use the last quote found by the LLM as the "primary" quote
          let primaryQuote = statement.quotes.at(-1);

          // If recordings are provided, try to use a quote from the most recent recording
          if (reverseChronologicalRecordings) {
            for (const recording of reverseChronologicalRecordings) {
              const quote = statement.quotes.find(
                (quote) => quote.recording_id === recording.id,
              );

              if (quote) {
                primaryQuote = quote;
                break;
              }
            }
          }

          return {
            ...statement,
            id: statement.id ?? `index:${index}`,
            statementIndex: index,
            primaryQuote,
          };
        },
      );
    }, [recordings, knowledgeBaseAdherence]);

  const handleAction = (key: Key): void => {
    if (onStatementClick === undefined) {
      return;
    }

    const item = allItems.find((item) => item.id === key);

    if (item === undefined) {
      return;
    }

    onStatementClick(item.statementIndex);
  };

  return (
    <Tabs className="rounded-lg border border-border-light bg-white shadow-xs">
      <TabList
        className="flex flex-wrap overflow-hidden border-b border-border-light"
        items={tabs}
      >
        {(tab) => (
          <Tab className="group mx-1.5 flex cursor-pointer items-center gap-1.5 border-b-2 border-transparent px-2.5 py-2 text-sm text-gray-700 hover:border-gray-700 hover:text-foreground selected:border-indigo-700 selected:text-indigo-700">
            {tab.correctness !== undefined ? (
              <KnowledgeBaseAdherenceCorrectnessIcon
                correctness={tab.correctness}
              />
            ) : null}
            <div className="truncate">{tab.label}</div>
            <div className="flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums group-selected:bg-indigo-100 group-selected:text-indigo-700">
              {filterItemsByCorrectness(allItems, tab.correctness).length}
            </div>
          </Tab>
        )}
      </TabList>

      <Collection items={tabs}>
        {(tab) => (
          <StatementsTabPanel
            id={tab.id}
            recordings={props.recordings}
            allItems={allItems}
            correctness={tab.correctness}
            onAction={onStatementClick !== undefined ? handleAction : undefined}
          />
        )}
      </Collection>
    </Tabs>
  );
};

interface StatementsTabPanelProps {
  id: Key;
  recordings: readonly AccountGradingRecording[] | undefined;
  allItems: readonly GradingKnowledgeBaseAdherenceStatementItem[];
  correctness: KnowledgeBaseAdherenceCorrectness | undefined;
  onAction?: (key: Key) => void;
}

const StatementsTabPanel: FC<StatementsTabPanelProps> = (props) => {
  const items = useMemo(() => {
    return groupStatementsByRecording(
      props.recordings,
      filterItemsByCorrectness(props.allItems, props.correctness),
    );
  }, [props.recordings, props.allItems, props.correctness]);

  return (
    <TabPanel id={props.id}>
      <GridList
        aria-label="Verifiable statements"
        className="divide-y divide-border-light"
        items={items}
        renderEmptyState={() => (
          <div className="px-4 py-2.5 text-sm text-muted-foreground">
            No statements.
          </div>
        )}
        onAction={props.onAction}
      >
        {(section) => (
          <GridListSection className="py-1.5">
            {section.recordedAt ? (
              <GridListHeader className="px-3 pt-1.5 pb-0.5 text-xs font-medium text-muted-foreground">
                {section.recordedAt.toLocaleString(DateTime.DATETIME_SHORT)}
              </GridListHeader>
            ) : null}

            <Collection items={section.items}>
              {(statement) => (
                <GridListItem
                  className="flex items-center gap-3 px-3 py-1.5 data-[react-aria-pressable]:cursor-pointer data-[react-aria-pressable]:hover:bg-blue-50"
                  textValue={statement.summary}
                >
                  <KnowledgeBaseAdherenceCorrectnessIcon
                    correctness={statement.correctness}
                  />

                  <div className="text-xs text-muted-foreground tabular-nums">
                    {statement.primaryQuote
                      ? Duration.fromMillis(
                          statement.primaryQuote.time,
                        ).toFormat("h:mm:ss")
                      : "?"}
                  </div>

                  <div className="truncate text-sm">{statement.summary}</div>
                </GridListItem>
              )}
            </Collection>
          </GridListSection>
        )}
      </GridList>
    </TabPanel>
  );
};

function filterItemsByCorrectness(
  allItems: readonly GradingKnowledgeBaseAdherenceStatementItem[],
  correctness: KnowledgeBaseAdherenceCorrectness | undefined,
) {
  if (correctness === undefined) {
    return allItems;
  }

  return allItems.filter((statement) => statement.correctness === correctness);
}

const OTHER_SECTION_ID = "other" as const;

function groupStatementsByRecording(
  recordings: readonly AccountGradingRecording[] | undefined,
  statements: readonly GradingKnowledgeBaseAdherenceStatementItem[],
): GradingKnowledgeBaseAdherenceStatementSection[] {
  // If at most one recording is provided, return all statements in a single section without a header
  if (recordings === undefined || recordings.length < 2) {
    return [
      {
        id: OTHER_SECTION_ID,
        items: statements,
      },
    ];
  }

  const sectionsById = new Map<
    string,
    MutableGradingKnowledgeBaseAdherenceStatementSection
  >();

  // Assign all the statements to a section
  for (const statement of statements) {
    const primaryQuoteRecordingId = statement.primaryQuote?.recording_id;

    // Find the recording that contains the primary quote for this statement
    const recording = primaryQuoteRecordingId
      ? recordings.find((recording) => recording.id === primaryQuoteRecordingId)
      : undefined;

    // Group it in that recording, or in "other"
    const sectionId = recording !== undefined ? recording.id : OTHER_SECTION_ID;

    // Find or create the section
    let section = sectionsById.get(sectionId);

    if (section === undefined) {
      // Try to parse the recording's recorded_at
      const recordedAt =
        recording !== undefined
          ? DateTime.fromISO(recording.recorded_at)
          : undefined;

      section = {
        id: sectionId,
        recordedAt: recordedAt?.isValid ? recordedAt : undefined,
        items: [],
      };
      sectionsById.set(sectionId, section);
    }

    section.items.push(statement);
  }

  // Collect the sections in order of the recordings
  const allPossibleSectionIds = [
    ...recordings.map((recording) => recording.id),
    OTHER_SECTION_ID,
  ];

  return allPossibleSectionIds
    .map((sectionId) => {
      const section = sectionsById.get(sectionId);

      if (sectionId === OTHER_SECTION_ID || !section) {
        return section;
      }

      // Sort the items by quote time
      return {
        ...section,
        items: section.items.sort((a, b) => {
          const aTime = a.primaryQuote?.time ?? Infinity;
          const bTime = b.primaryQuote?.time ?? Infinity;
          return aTime - bTime;
        }),
      };
    })
    .filter(isNotNullNotUndefined);
}
